import tw from 'twin.macro';

import { TagActionCellType } from '@insights_module/types/Megaphone';

import { ThreeDots } from '@icons/ThreeDots';
import { EyeIcon } from '@icons/EyeIcon';
import { IntegrationType } from '@insights_module/types/Integrations';
import { FiltersIcon } from '@icons/FiltersIcon';
import { ClipboardIcon } from '@icons/ClipboardIcon';
import { CloseIcon } from '@icons/CloseIcon';
import { EditIcon } from '@icons/EditIcon';
import { DuplicateIcon } from '@icons/DuplicateIcon';
import { IOrganizationUser } from '@account_module/types/Organizations';
import { USER_ROLE_KEY } from '@account_module/constants/userKeys';
import { ExportIcon } from '@icons/ExportIcon';
import { Loader } from '@components/Loader';

const CellWrapper = tw.div`flex items-center justify-end gap-2 w-auto relative px-4`;
const ActionButton = tw.button`border border-solid border-gray-1 rounded p-1`;
const CtaButtonsWrapper = tw.div`bg-neutral-background border border-solid border-gray-1 rounded absolute bottom-0 translate-y-[calc(100% + 0.3rem)] right-0 z-50 p-1 shadow-dropdown`;
const CtaButton = tw.button`w-full text-left text-sm flex items-center justify-start whitespace-nowrap py-1 pl-[6px] pr-3  hover:bg-gray-2 rounded-sm disabled:cursor-not-allowed`;
const Divider = tw.div`w-full h-[1px] bg-gray-1 my-1`;

export const ActionCell = (
  integrationType: IntegrationType,
  item: TagActionCellType,
  selectedAction: string | null,
  currentUser: IOrganizationUser,
  tagIdsToExport: string[],
  setSelectedAction: (id: string | null) => void,
  handleCopyToClipboard: (val: string | null) => void,
  setIsDescriptionModalOpen: (val: boolean) => void,
  handleDeleteTag?: (id: string) => void,
  handleApplyTagToInsights?: (val: string) => void,
  setIsEditTagModalOpen?: (val: boolean) => void,
  setTagToEdit?: (val: string) => void,
  handleDuplicateTag?: (id: string) => void,
  isExportShowSummaryEnabled?: boolean,
  isExportShowSummaryInProgress?: boolean,
  handleExportShowSummary?: (id: string) => void,
  // We pass the translation instance because we cannot call hooks in this component.
  // The instance type is not exported from i18n, so we use the "any" type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t?: any
) => {
  const { id, tag_id, title } = item;

  const isAdmin = currentUser.role === USER_ROLE_KEY.admin;
  const isViewer = currentUser.role === USER_ROLE_KEY.viewer;

  const onActionClick = () => {
    if (!setSelectedAction) return;

    setSelectedAction(selectedAction === id ? null : id);
  };

  const onClickDuplicateTag = () => {
    if (isViewer) return;
    handleDuplicateTag && handleDuplicateTag(id);
  };

  const onClickDelete = () => {
    if (!isAdmin) return;
    handleDeleteTag && handleDeleteTag(tag_id);
  };

  const onClickEdit = () => {
    if (isViewer) return;
    setTagToEdit && setTagToEdit(id);
    setIsEditTagModalOpen && setIsEditTagModalOpen(true);
  };

  const onClickApplyToInsights = () => {
    setSelectedAction(null);
    handleApplyTagToInsights && handleApplyTagToInsights(id);
  };

  const onClickCopyTitle = () => {
    handleCopyToClipboard && handleCopyToClipboard(title);
  };

  const onClickCopyCode = () => {
    handleCopyToClipboard && handleCopyToClipboard(tag_id);
  };

  const onClickEditDescription = () => {
    if (isViewer) return;
    setTagToEdit && setTagToEdit(id);
    setIsDescriptionModalOpen && setIsDescriptionModalOpen(true);
  };

  const onClickExportShowSummary = () => {
    handleExportShowSummary && handleExportShowSummary(id);
  };

  return (
    <CellWrapper>
      <ActionButton
        onClick={onActionClick}
        className='integration-action-btn'
        css={[selectedAction === id && tw`bg-gray-1`]}>
        {tagIdsToExport.includes(id) ? (
          <Loader size='sm' theme='dark' />
        ) : (
          <ThreeDots tw='fill-text-and-icon stroke-text-and-icon' />
        )}
      </ActionButton>

      {selectedAction === id && (
        <CtaButtonsWrapper>
          <CtaButton tw='text-text-and-icon-muted' onClick={onClickApplyToInsights}>
            <EyeIcon tw='mr-1 w-4 stroke-text-and-icon-muted fill-none' />
            {t('insights.integrations.ihm_starship.open_in_insights_cta')}
          </CtaButton>

          {integrationType === 'megaphone' && (
            <>
              <CtaButton tw='text-text-and-icon-muted' disabled={isViewer} onClick={onClickEdit}>
                <FiltersIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
                {t('insights.integrations.megaphone.tags_cta.view_filter')}
              </CtaButton>
              <CtaButton
                tw='text-text-and-icon-muted'
                disabled={isViewer}
                onClick={onClickDuplicateTag}>
                <DuplicateIcon tw='mr-1 w-4 stroke-text-and-icon-muted fill-none' />
                {t('insights.integrations.megaphone.tags_cta.duplicate_tag')}
              </CtaButton>
            </>
          )}

          <CtaButton tw='text-text-and-icon-muted' onClick={onClickCopyTitle}>
            <ClipboardIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
            {t('insights.integrations.megaphone.tags_cta.copy_name')}
          </CtaButton>
          <CtaButton tw='text-text-and-icon-muted' onClick={onClickCopyCode}>
            <ClipboardIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
            {t('insights.integrations.megaphone.tags_cta.copy_code')}
          </CtaButton>

          {integrationType === 'megaphone' && (
            <CtaButton
              tw='text-text-and-icon-muted'
              disabled={isViewer}
              onClick={onClickEditDescription}>
              <EditIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
              {t('insights.integrations.megaphone.tags_cta.edit_description')}
            </CtaButton>
          )}

          {integrationType === 'ihm' && (
            <CtaButton tw='text-text-and-icon-muted' disabled={isViewer} onClick={onClickEdit}>
              <EditIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
              {t('insights.integrations.megaphone.tags_cta.edit_tag')}
            </CtaButton>
          )}

          {isExportShowSummaryEnabled && (
            <CtaButton
              tw='text-text-and-icon-muted'
              disabled={isExportShowSummaryInProgress}
              onClick={onClickExportShowSummary}>
              <ExportIcon tw='mr-1 w-4 stroke-text-and-icon-muted' />
              {t('insights.integrations.megaphone.tags_cta.export_summary_csv')}
            </CtaButton>
          )}

          <Divider />
          <CtaButton
            tw='text-error-text-icon hover:bg-error-bg fill-error-text-icon'
            disabled={!isAdmin}
            onClick={onClickDelete}>
            <CloseIcon tw='mr-1 w-4 fill-inherit' />
            {t('insights.integrations.megaphone.tags_cta.delete_tag')}
          </CtaButton>
        </CtaButtonsWrapper>
      )}
    </CellWrapper>
  );
};
